import { uniq } from 'lodash';
import {
  allowedTagsSelector,
  allowedWavesSelector,
} from '../../auth/selectors/auth.selectors';
import { expertMatchesUserSelector } from '../../delineation/selectors/delineation.selectors';
import { TagLabel, WaveTag } from '../../wave/types/tag.type';
import {
  BeatWaveType,
  InvalidWaveType,
  ServerWaveType,
  Wave,
  WaveProcessor,
} from '../../wave/types/wave.type';
import {
  getAllowedTagContainers,
  getAssociativeTagContainers,
  getOptionalTagContainers,
  isTagContainersEqual,
  associativeTagSet,
  requiredTagSet,
  optionalTagSet,
} from '../../wave/utils/tags.utils';
import { invalidTypes, isWaveOfType } from '../../wave/utils/wave.util';

export const wavesFilteredByPreferences: WaveProcessor = (
  waves: Wave[],
  getState,
) => {
  const isUserSelected = expertMatchesUserSelector(getState());
  const allowWaveAnnotations = allowedWavesSelector('wavesToAnnotate')(
    getState(),
  );
  const allowWaveDisplay = allowedWavesSelector('wavesToDisplay')(getState());
  const allowTagDisplay = allowedTagsSelector('tagsToDisplay')(getState());
  const allowTagAnnotations = allowedTagsSelector('tagsToAnnotate')(getState());

  // If it is the delineation of the account user then we need to show the allowed waves and tags.
  // Otherwise we use the ones from waves/tags to display
  if (isUserSelected) {
    return filter(waves, allowWaveAnnotations, allowTagAnnotations);
  } else {
    return filter(waves, allowWaveDisplay, allowTagDisplay);
  }
};

// Filter waves according to the allowed types
const filter = (
  waves: Wave[],
  allowedWaves: Set<ServerWaveType>,
  allowedTags: WaveTag,
): Wave[] => {
  return waves
    .filter((wave) => {
      // if it's an invalid wave from the back comes with type 'INV' with tags:
      // [calibration, artifact, noise].
      // For more the sake of simplicity we set the types of each wave to calibration, noise, artifact respectively
      // to manipulate them easier.
      // So if the allowed waves are one of the invalid types we compare the allowed waves with 'INV'
      // to check if they are should be filtered or not
      if (isWaveOfType(wave, ...invalidTypes)) {
        return allowedWaves.has('INV');
      }
      return allowedWaves.has(wave.type as BeatWaveType);
    })
    .map((wave) => {
      // If invalid waves are allowed there is no need to filter their tags
      // since there is no tag selection for invalid tags on minda
      if (isWaveOfType(wave, ...invalidTypes)) {
        return wave;
      }

      const tags = filterTags(wave.type, allowedTags, wave.tags);

      return { ...wave, tags };
    });
};

// Filter tags according to the allowed types
const filterTags = (
  waveType: BeatWaveType | InvalidWaveType,
  allowedTags: WaveTag,
  tags: TagLabel[],
) => {
  const requiredTags = uniq(
    getAllowedTagContainers(allowedTags, waveType)
      .filter((tagContainer) =>
        isTagContainersEqual(tagContainer?.tags, tags, [
          associativeTagSet,
          optionalTagSet,
        ]),
      )
      .flatMap(({ tags }) => tags),
  );

  const associativeTags = getAssociativeTagContainers(allowedTags, waveType)
    .filter((tagContainer) =>
      isTagContainersEqual(tagContainer?.tags, tags, [
        requiredTagSet,
        optionalTagSet,
      ]),
    )
    .flatMap(({ tags }) => tags);

  const optionalTags = getOptionalTagContainers(waveType, requiredTags)
    .filter((tagContainer) =>
      isTagContainersEqual(tagContainer.tags, tags, [
        requiredTagSet,
        associativeTagSet,
      ]),
    )
    .flatMap(({ tags }) => tags);

  const filteredTags = requiredTags
    .concat(associativeTags)
    .concat(optionalTags);

  return uniq(filteredTags);
};
